<template>
  <div class="mc-root">
    <!-- Commercial loan -->
    <div class="mc-section">
      <div class="mc-title">{{ $t('tool.MortgageCalculator.commercial') }}</div>
      <div class="mc-form">
        <div class="mc-row">
          <label>{{ $t('tool.MortgageCalculator.amount') }}</label>
          <el-input v-model.number="cAmount" type="number" size="small" :min="0" :placeholder="$t('tool.MortgageCalculator.phAmount')" @input="calc" />
        </div>
        <div class="mc-row">
          <label>{{ $t('tool.MortgageCalculator.years') }}</label>
          <el-select v-model="cYears" size="small" style="width:100%;" @change="calc">
            <el-option v-for="y in yearOptions" :key="y" :label="$t('tool.MortgageCalculator.yearLabel',{y:y,n:y*12})" :value="y" />
          </el-select>
        </div>
        <div class="mc-row">
          <label>{{ $t('tool.MortgageCalculator.rate') }}</label>
          <el-input v-model.number="cRate" type="number" size="small" :min="0.01" :step="0.01" :placeholder="$t('tool.MortgageCalculator.phRate')" @input="calc" />
        </div>
      </div>
    </div>

    <!-- Provident fund loan -->
    <div class="mc-section">
      <div class="mc-title">{{ $t('tool.MortgageCalculator.fund') }}</div>
      <div class="mc-form">
        <div class="mc-row">
          <label>{{ $t('tool.MortgageCalculator.amount') }}</label>
          <el-input v-model.number="gAmount" type="number" size="small" :min="0" :placeholder="$t('tool.MortgageCalculator.phAmount')" @input="calc" />
        </div>
        <div class="mc-row">
          <label>{{ $t('tool.MortgageCalculator.years') }}</label>
          <el-select v-model="gYears" size="small" style="width:100%;" @change="calc">
            <el-option v-for="y in yearOptions" :key="y" :label="$t('tool.MortgageCalculator.yearLabel',{y:y,n:y*12})" :value="y" />
          </el-select>
        </div>
        <div class="mc-row">
          <label>{{ $t('tool.MortgageCalculator.rate') }}</label>
          <el-input v-model.number="gRate" type="number" size="small" :min="0.01" :step="0.01" :placeholder="$t('tool.MortgageCalculator.phRate')" @input="calc" />
        </div>
      </div>
    </div>

    <!-- Method -->
    <div class="mc-method">
      <label>{{ $t('tool.MortgageCalculator.method') }}</label>
      <el-radio-group v-model="method" size="small" @change="calc">
        <el-radio-button label="equal">{{ $t('tool.MortgageCalculator.methodEI') }}</el-radio-button>
        <el-radio-button label="principal">{{ $t('tool.MortgageCalculator.methodEP') }}</el-radio-button>
      </el-radio-group>
    </div>

    <!-- Results -->
    <div class="mc-result" v-if="result">
      <div class="mc-cards">
        <div class="mc-card">
          <div class="mc-card-val">{{ result.monthly }}</div>
          <div class="mc-card-label">{{ $t('tool.MortgageCalculator.monthly') }}</div>
        </div>
        <div class="mc-card">
          <div class="mc-card-val">{{ result.totalInterest }}</div>
          <div class="mc-card-label">{{ $t('tool.MortgageCalculator.interest') }}</div>
        </div>
        <div class="mc-card">
          <div class="mc-card-val">{{ result.totalPayment }}</div>
          <div class="mc-card-label">{{ $t('tool.MortgageCalculator.total') }}</div>
        </div>
      </div>

      <div class="mc-breakdown">
        <div class="mc-sub"><span class="mc-dot mc-dot-c" /> {{ $t('tool.MortgageCalculator.commercialLoan') }} <strong>{{ result.cMonthly || '--' }}</strong> {{ $t('tool.MortgageCalculator.perMonth') }}</div>
        <div class="mc-sub"><span class="mc-dot mc-dot-g" /> {{ $t('tool.MortgageCalculator.fundLoan') }} <strong>{{ result.gMonthly || '--' }}</strong> {{ $t('tool.MortgageCalculator.perMonth') }}</div>
        <div class="mc-sub" v-if="result.monthly === '--'" style="margin-top:4px;color:#909399;">{{ $t('tool.MortgageCalculator.principalNote') }}</div>
      </div>

      <!-- Detail table -->
      <el-collapse v-if="result.details && result.details.length" style="margin-top:16px;">
        <el-collapse-item name="detail">
          <template slot="title"><span class="mc-detail-title">{{ $t('tool.MortgageCalculator.detailTitle',{n:result.totalPeriods}) }}</span></template>
          <div class="mc-table-wrap">
            <table class="mc-table">
              <thead><tr><th>{{ $t('tool.MortgageCalculator.colPeriod') }}</th><th>{{ $t('tool.MortgageCalculator.colPayment') }}</th><th>{{ $t('tool.MortgageCalculator.colPrincipal') }}</th><th>{{ $t('tool.MortgageCalculator.colInterest') }}</th><th>{{ $t('tool.MortgageCalculator.colBalance') }}</th></tr></thead>
              <tbody>
                <tr v-for="r in result.details" :key="r.period">
                  <td>{{ r.period }}</td><td>{{ r.payment }}</td><td>{{ r.principal }}</td><td>{{ r.interest }}</td><td>{{ r.balance }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>

    <div class="mc-empty" v-else>{{ $t('tool.MortgageCalculator.emptyHint') }}</div>
  </div>
</template>

<script>
function fmt(n) {
  if (typeof n !== 'number' || isNaN(n)) return '0'
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function calcLoan(P, rate, years, method) {
  if (!P || !rate || !years || P <= 0 || rate <= 0 || years <= 0) return null
  var mr = rate / 100 / 12, N = years * 12
  var details = [], totalInterest = 0
  var isEqual = method === 'equal'

  if (isEqual) {
    var monthly = (P * mr * Math.pow(1 + mr, N)) / (Math.pow(1 + mr, N) - 1)
    var balance = P
    for (var i = 1; i <= N; i++) {
      var interest = balance * mr
      var principal = monthly - interest
      balance -= principal
      if (balance < 0.01) balance = 0
      totalInterest += interest
      if (i <= 12 || i > N - 12 || i % 12 === 0)
        details.push({ period: i, payment: fmt(monthly), principal: fmt(principal), interest: fmt(interest), balance: fmt(balance) })
    }
    return { monthly: monthly, totalInterest: totalInterest, totalPayment: P + totalInterest, totalPeriods: N, details: details }
  } else {
    var firstPrincipal = P / N
    var bal = P, firstPay = 0, lastPay = 0
    for (var j = 1; j <= N; j++) {
      var int2 = bal * mr, pay2 = firstPrincipal + int2
      bal -= firstPrincipal
      if (bal < 0.01) bal = 0
      totalInterest += int2
      if (j === 1) firstPay = pay2
      if (j === N) lastPay = pay2
      if (j <= 12 || j > N - 12 || j % 12 === 0)
        details.push({ period: j, payment: fmt(pay2), principal: fmt(firstPrincipal), interest: fmt(int2), balance: fmt(bal) })
    }
    return { monthly: null, firstMonthly: firstPay, lastMonthly: lastPay, totalInterest: totalInterest, totalPayment: P + totalInterest, totalPeriods: N, details: details }
  }
}

export default {
  name: 'MortgageCalculator',
  props: { config: { type: Object, default: function() { return {} } } },
  data: function() {
    return {
      cAmount: 70, cYears: 30, cRate: 3.25,
      gAmount: 30, gYears: 30, gRate: 2.85,
      method: 'equal',
      result: null
    }
  },
  computed: {
    yearOptions: function() {
      var opts = []; for (var i = 1; i <= 30; i++) opts.push(i); return opts
    }
  },
  created: function() { this.calc() },
  methods: {
    calc: function() {
      var cr = calcLoan(this.cAmount * 10000, this.cRate, this.cYears, this.method)
      var gr = calcLoan(this.gAmount * 10000, this.gRate, this.gYears, this.method)

      if (!cr && !gr) { this.result = null; return }

      var isEqual = this.method === 'equal'
      var monthly = null, totalInterest = 0, totalPayment = 0
      var cMonthlyStr = null, gMonthlyStr = null

      // monthly
      if (isEqual) {
        var m = 0
        if (cr) m += cr.monthly
        if (gr) m += gr.monthly
        monthly = fmt(m)
      } else {
        // 等额本金月供不固定 → 顶部显示 --
        monthly = '--'
      }

      // interest & total
      if (cr) { totalInterest += cr.totalInterest; totalPayment += cr.totalPayment }
      if (gr) { totalInterest += gr.totalInterest; totalPayment += gr.totalPayment }

      // 分项月供展示
      if (cr) {
        cMonthlyStr = isEqual ? fmt(cr.monthly) : fmt(cr.firstMonthly) + ' ~ ' + fmt(cr.lastMonthly)
      }
      if (gr) {
        gMonthlyStr = isEqual ? fmt(gr.monthly) : fmt(gr.firstMonthly) + ' ~ ' + fmt(gr.lastMonthly)
      }

      // merge details
      var details = []
      var maxN = Math.max(cr ? cr.totalPeriods : 0, gr ? gr.totalPeriods : 0)
      for (var i = 1; i <= maxN; i++) {
        if (i <= 12 || i > maxN - 12 || i % 12 === 0) {
          var cd = cr && i <= cr.totalPeriods ? cr.details.find(function(d) { return d.period === i }) : null
          var gd = gr && i <= gr.totalPeriods ? gr.details.find(function(d) { return d.period === i }) : null
          var pay = (cd ? parseFloat(cd.payment.replace(/,/g, '')) : 0) + (gd ? parseFloat(gd.payment.replace(/,/g, '')) : 0)
          var pri = (cd ? parseFloat(cd.principal.replace(/,/g, '')) : 0) + (gd ? parseFloat(gd.principal.replace(/,/g, '')) : 0)
          var int = (cd ? parseFloat(cd.interest.replace(/,/g, '')) : 0) + (gd ? parseFloat(gd.interest.replace(/,/g, '')) : 0)
          var bal = (cd ? parseFloat(cd.balance.replace(/,/g, '')) : 0) + (gd ? parseFloat(gd.balance.replace(/,/g, '')) : 0)
          details.push({ period: i, payment: fmt(pay), principal: fmt(pri), interest: fmt(int), balance: fmt(bal) })
        }
      }

      this.result = {
        monthly: monthly,
        totalInterest: fmt(totalInterest),
        totalPayment: fmt(totalPayment),
        totalPeriods: maxN,
        cMonthly: cMonthlyStr,
        gMonthly: gMonthlyStr,
        details: details
      }
    },
    fmt: fmt,
    doAction: function(a) {
      if (a === 'clear') {
        this.cAmount = 0; this.cRate = 3.25; this.cYears = 30
        this.gAmount = 0; this.gRate = 2.85; this.gYears = 30
        this.result = null
      }
    }
  }
}
</script>

<style>
.mc-root { width: 100%; }

.mc-section { margin-bottom: 16px; }
.mc-title { font-size: 14px; font-weight: 700; color: #303133; margin-bottom: 10px; padding-left: 10px; border-left: 3px solid #4e7cff; }
.mc-form { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px 16px; }
.mc-row { display: flex; flex-direction: column; gap: 4px; }
.mc-row label { font-size: 12px; font-weight: 600; color: #909399; }

.mc-method { margin-bottom: 20px; display: flex; align-items: center; gap: 12px; }
.mc-method label { font-size: 13px; font-weight: 600; color: #606266; }

.mc-cards { display: flex; gap: 14px; }
.mc-card { flex: 1; background: #f5f7fa; border-radius: 10px; padding: 16px; text-align: center; }
.mc-card-val { font-size: 20px; font-weight: 700; color: #4e7cff; word-break: break-all; }
.mc-card-label { font-size: 12px; color: #909399; margin-top: 4px; }

.mc-breakdown { display: flex; gap: 20px; margin-top: 14px; padding: 12px 16px; background: #fafbfc; border-radius: 8px; }
.mc-sub { font-size: 13px; color: #606266; display: flex; align-items: center; gap: 6px; }
.mc-sub strong { color: #303133; }
.mc-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.mc-dot-c { background: #4e7cff; }
.mc-dot-g { background: #67c23a; }

.mc-detail-title { font-size: 13px; font-weight: 600; color: #606266; }

.mc-table-wrap { max-height: 420px; overflow: auto; }
.mc-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.mc-table th, .mc-table td { padding: 6px 10px; text-align: right; border-bottom: 1px solid #ebeef5; }
.mc-table th { background: #fafbfc; color: #606266; font-weight: 600; position: sticky; top: 0; }
.mc-table td { color: #303133; font-family: monospace; }
.mc-table td:first-child, .mc-table th:first-child { text-align: center; }

.mc-empty { text-align: center; padding: 40px; color: #c0c4cc; font-size: 14px; }

@media (max-width: 640px) {
  .mc-form { grid-template-columns: 1fr; }
  .mc-cards, .mc-breakdown { flex-direction: column; }
}
</style>
